import { BarChart3Icon, InfoIcon } from "lucide-react";
import { type ReactNode, useId } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ChartSwatchLegend,
  ChartTooltipFrame,
  ChartTooltipRow,
} from "@/components/ui/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CHART_PALETTE, chartSeriesColor } from "@/lib/chart-palette";
import {
  compactNumber,
  type MetricDisplayKind,
} from "./record-dashboard-model";

// One accent for every single-series mark (bars, lines, areas) so charts on
// one dashboard read as one system rather than colour-coding by chart type.
export const DASHBOARD_SERIES_COLOR = "var(--chart-1)";
// Fixed identity order starting from the accent; the last slot is the muted
// overflow colour shared by every series past the hues. Assign with
// dashboardSeriesColor — colours clamp, they never cycle.
export const DASHBOARD_PALETTE = CHART_PALETTE;

// Shared recharts frame so adjacent widgets align: identical margins, grid,
// axis gutter, and mark sizing wherever the chart type doesn't genuinely
// need to differ (stacked bars are wider; grouped clusters are narrower).
export const DASHBOARD_CHART_FRAME = {
  margin: { top: 18, right: 10, bottom: 2, left: 0 },
  labelledMargin: { top: 24, right: 10, bottom: 2, left: 0 },
  yAxisWidth: 44,
  gridOpacity: 0.58,
  barRadius: [4, 4, 1, 1] as [number, number, number, number],
  maxBarSize: 44,
  groupedMaxBarSize: 34,
  stackedMaxBarSize: 54,
} as const;

export const dashboardSeriesColor = chartSeriesColor;

const SKELETON_BAR_HEIGHTS = [42, 68, 54, 82, 64, 76] as const;

export function DashboardWidget({
  title,
  description,
  unit,
  meta,
  actions,
  children,
  className,
}: {
  title: string;
  description?: string;
  unit?: string;
  meta?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const titleId = useId();
  return (
    <Card
      className={cn(
        // Charts use an elevated surface; table widgets override the background
        // to match their rows and the standalone table canvas.
        "flex h-full min-h-full flex-col overflow-hidden rounded-lg border-border/70 bg-surface shadow-none",
        className,
      )}
      role="region"
      aria-labelledby={titleId}
    >
      <div
        data-slot="dashboard-widget-header"
        className="flex min-h-9 min-w-0 items-center gap-1.5 px-3.5 py-2"
      >
        <div className="flex min-w-0 flex-1 items-baseline gap-1.5 overflow-hidden">
          <span
            id={titleId}
            className="block min-w-0 truncate whitespace-nowrap text-sm font-semibold leading-4 text-foreground"
            title={title}
          >
            {title}
          </span>
          {unit ? (
            <span
              data-slot="dashboard-widget-unit"
              className="shrink-0 text-3xs font-medium uppercase tracking-[0.08em] text-tertiary-foreground"
            >
              {unit}
            </span>
          ) : null}
        </div>
        {meta ? (
          <span
            data-slot="dashboard-widget-meta"
            className="min-w-0 max-w-[45%] shrink truncate whitespace-nowrap text-3xs leading-4 tabular-nums text-muted-foreground"
            title={meta}
          >
            {meta}
          </span>
        ) : null}
        {description ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`${title} details`}
                  className="text-tertiary-foreground hover:text-foreground"
                />
              }
            >
              <InfoIcon className="size-3.5" aria-hidden />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="end"
              sideOffset={6}
              className="max-w-72 text-pretty leading-4"
            >
              {description}
            </TooltipContent>
          </Tooltip>
        ) : null}
        {actions}
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-3.5 pb-3.5">
        {children}
      </div>
    </Card>
  );
}

export function DashboardChart({
  label,
  summary,
  clickable = false,
  children,
  className,
}: {
  label: string;
  summary?: string;
  /** Marks category marks as click-to-filter targets (pointer cursor). */
  clickable?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const summaryId = useId();
  return (
    <div
      className={cn(
        "mlx-dashboard-chart relative min-h-0 flex-1 overflow-hidden rounded-lg",
        className,
      )}
      data-clickable={clickable ? "" : undefined}
      role="group"
      aria-label={label}
      aria-describedby={summary ? summaryId : undefined}
    >
      {children}
      {summary ? (
        <span id={summaryId} className="sr-only">
          {summary}
        </span>
      ) : null}
    </div>
  );
}

// The product-wide swatch legend, re-exported under the dashboard name its
// call sites and tests already use.
export const DashboardLegend = ChartSwatchLegend;

export type DashboardSkeletonVariant = "chart" | "metric" | "table";

export function DashboardWidgetSkeleton({
  label = "Loading widget data",
  variant = "chart",
}: {
  label?: string;
  variant?: DashboardSkeletonVariant;
}) {
  if (variant === "metric") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col justify-center gap-3 overflow-hidden rounded-lg"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">{label}</span>
        <div
          className="animate-pulse space-y-3 motion-reduce:animate-none"
          aria-hidden
        >
          <span className="block h-8 w-2/5 rounded bg-muted/70" />
          <span className="block h-2.5 w-3/5 rounded bg-muted/45" />
        </div>
      </div>
    );
  }
  if (variant === "table") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden rounded-lg pt-1"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">{label}</span>
        <div
          className="animate-pulse space-y-2.5 motion-reduce:animate-none"
          aria-hidden
        >
          <span className="block h-2.5 w-full rounded bg-muted/70" />
          {[0.55, 0.45, 0.6, 0.4, 0.5].map((opacity, index) => (
            <span
              key={index}
              className="block h-2.5 rounded bg-muted"
              style={{ width: `${92 - index * 6}%`, opacity }}
            />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex min-h-0 flex-1 flex-col justify-end gap-3 overflow-hidden rounded-lg border border-border/45 bg-muted/10 px-3 pb-3 pt-6"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">{label}</span>
      <div
        className="flex flex-1 items-end gap-2 animate-pulse motion-reduce:animate-none"
        aria-hidden
      >
        {SKELETON_BAR_HEIGHTS.map((height, index) => (
          <span
            key={index}
            className="min-w-0 flex-1 rounded-t bg-muted"
            style={{ height: `${height}%`, opacity: 0.48 + index * 0.055 }}
          />
        ))}
      </div>
      <div
        className="grid grid-cols-4 gap-3 animate-pulse motion-reduce:animate-none"
        aria-hidden
      >
        <span className="h-2 rounded bg-muted/75" />
        <span className="h-2 rounded bg-muted/55" />
        <span className="h-2 rounded bg-muted/65" />
        <span className="h-2 rounded bg-muted/45" />
      </div>
    </div>
  );
}

export function EmptyChart({
  children,
  title = "Nothing to chart yet",
}: {
  children: ReactNode;
  title?: string;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center px-5 text-center"
      role="note"
    >
      <span className="mb-2 flex size-7 items-center justify-center rounded-full bg-muted/60 text-tertiary-foreground">
        <BarChart3Icon className="size-3.5" />
      </span>
      <span className="text-xs font-medium text-foreground/75">{title}</span>
      <span className="mt-1 max-w-64 text-2xs leading-4 text-muted-foreground">
        {children}
      </span>
    </div>
  );
}

type DashboardTooltipItem = {
  value?: number | string;
  name?: string;
  color?: string;
  fill?: string;
  payload?: Record<string, unknown>;
};

export function DashboardTooltip({
  active,
  label,
  payload,
  currency,
  displayKind,
  shareOfTotal,
  includeStackSummary = true,
}: {
  active?: boolean;
  label?: string | number;
  payload?: DashboardTooltipItem[];
  currency?: boolean;
  displayKind?: MetricDisplayKind;
  /** Radial charts: show each slice's share of this total. */
  shareOfTotal?: number;
  /** Stack totals/deltas describe every series; hide them while some are toggled off. */
  includeStackSummary?: boolean;
}) {
  if (!active || !payload?.length) return null;
  const stackTotal = includeStackSummary ? stackedTooltipTotal(payload) : null;
  const periodDelta = includeStackSummary ? stackedTooltipDelta(payload) : null;
  return (
    <ChartTooltipFrame label={label}>
      {payload.map((item, index) => (
        <ChartTooltipRow
          key={index}
          color={item.color ?? item.fill ?? dashboardSeriesColor(index)}
          name={item.name ?? "Value"}
          value={compactNumber(
            Number(item.value ?? 0),
            displayKind ?? Boolean(currency),
          )}
          valueSuffix={
            shareOfTotal
              ? `${Math.round((Number(item.value ?? 0) / shareOfTotal) * 100)}%`
              : undefined
          }
        />
      ))}
      {stackTotal !== null ? (
        <div className="mt-1 border-t border-border/50 pt-1">
          <ChartTooltipRow
            name="Total"
            value={compactNumber(stackTotal, displayKind ?? Boolean(currency))}
          />
        </div>
      ) : null}
      {periodDelta !== null ? (
        <div className="flex items-center gap-2 py-0.5">
          <span className="size-2 shrink-0" aria-hidden />
          <span className="text-muted-foreground">vs prior period</span>
          <span
            className={cn(
              "ml-auto pl-3 font-mono font-medium tabular-nums",
              periodDelta >= 0 ? "text-success" : "text-destructive",
            )}
          >
            {`${periodDelta >= 0 ? "+" : ""}${Math.round(periodDelta * 100)}%`}
          </span>
        </div>
      ) : null}
    </ChartTooltipFrame>
  );
}

// Stacked payloads carry the point's total and period delta on every entry;
// surface them once as summary rows instead of chip chrome under the chart.
function stackedTooltipTotal(payload: DashboardTooltipItem[]): number | null {
  if (payload.length < 2) return null;
  const total = payload[0]?.payload?.total;
  return typeof total === "number" && Number.isFinite(total) ? total : null;
}

function stackedTooltipDelta(payload: DashboardTooltipItem[]): number | null {
  const delta = payload[0]?.payload?.yoyDelta;
  return typeof delta === "number" && Number.isFinite(delta) ? delta : null;
}
